import React, { useEffect, useMemo, useState } from "react";
import { Check, CreditCard, Loader2, Lock, Globe2, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/lib/currency";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { PayPalMePayModal, PayPalAwaitingBanner, PayPalAdminApprovals } from "@/components/billing/PayPalManual";

// Inline brand-mark icons (no external deps) so each gateway has a recognizable badge.

function XeroConnectPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [checking, setChecking] = useState("");

  const load = () => api.get("/billing/xero/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadInvoices = () => api.get("/billing/xero/invoices").then((r) => setInvoices(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadInvoices(); }, []);

  useEffect(() => {
    const onCreated = () => loadInvoices();
    window.addEventListener("bfa:xero-invoice-created", onCreated);
    return () => window.removeEventListener("bfa:xero-invoice-created", onCreated);
  }, []);

  const checkPayment = async (inv) => {
    setChecking(inv.xero_invoice_id);
    try {
      const { data } = await api.post(`/billing/xero/invoice/${inv.xero_invoice_id}/refresh`);
      if (data.activated) {
        toast.success("Payment confirmed — your plan is now active!", { duration: 8000 });
        setTimeout(() => window.location.reload(), 1200);
      } else if (data.status === "PAID") {
        toast.success("Invoice already paid.");
      } else {
        toast.info(`Not paid yet — status: ${data.status}, due: $${data.amount_due}`);
      }
      loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status check failed");
    } finally { setChecking(""); }
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("xero") === "connected") {
      toast.success("Xero connected — your organisation is linked!");
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (q.get("xero") === "error") {
      toast.error(`Xero connection failed (${q.get("reason") || "unknown"})`, { duration: 8000 });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/billing/xero/login");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Xero connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/billing/xero/disconnect"); toast.success("Xero disconnected"); await load(); }
    catch { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  return (
    <div className="mt-4 space-y-2">
      <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-3 flex flex-wrap items-center gap-3" data-testid="xero-connect-panel">
      <div className="text-xs text-sky-100 flex-1 min-w-[220px]">
        {status.connected ? (
          <>Connected to Xero organisation <span className="font-semibold text-sky-200">{status.tenant_name || "—"}</span>. Invoices can now be raised in your Xero account.</>
        ) : (
          <>Xero is configured. Connect your Xero organisation to enable invoice-based billing.</>
        )}
      </div>
      {status.connected ? (
        <button
          onClick={disconnect}
          disabled={busy}
          data-testid="xero-disconnect-btn"
          className="text-xs rounded-md border border-line bg-bg px-3 py-1.5 text-ink-secondary hover:text-red-300 hover:border-red-500/40 transition disabled:opacity-50"
        >
          Disconnect
        </button>
      ) : (
        <button
          onClick={connect}
          disabled={busy}
          data-testid="xero-connect-btn"
          className="text-xs rounded-md bg-sky-500 text-black font-semibold px-4 py-2 hover:brightness-110 transition disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
          Connect Xero
        </button>
      )}
      </div>
      {invoices.length > 0 && (
        <div className="rounded-md border border-line bg-bg-surface p-3" data-testid="xero-invoices-list">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-2">Xero invoices</div>
          <div className="space-y-1.5">
            {invoices.map((inv) => (
              <div key={inv.xero_invoice_id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`xero-inv-${inv.xero_invoice_id}`}>
                <span className="font-mono text-ink-primary">{inv.invoice_number || inv.xero_invoice_id.slice(0, 8)}</span>
                <span className="text-ink-muted">{inv.plan_id} · {inv.cycle} · ${inv.amount}</span>
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${inv.status === "PAID" ? "bg-emerald-500/10 text-emerald-300" : "bg-brand-amber/10 text-brand-amber"}`}>
                  {inv.status}
                </span>
                <span className="flex-1" />
                {inv.online_url && (
                  <a href={inv.online_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">View</a>
                )}
                {inv.status !== "PAID" && (
                  <button
                    onClick={() => checkPayment(inv)}
                    disabled={checking === inv.xero_invoice_id}
                    data-testid={`xero-inv-check-${inv.xero_invoice_id}`}
                    className="rounded border border-line bg-bg px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary transition disabled:opacity-50"
                  >
                    {checking === inv.xero_invoice_id ? "Checking…" : "Check payment"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const GatewayMark = ({ id }) => {
  const common = "h-4 w-4";
  if (id === "stripe") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>S</span>;
  if (id === "paypal") return <span className={`font-display text-[12px] font-black italic tracking-tight ${common}`}>PP</span>;
  if (id === "razorpay") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>R</span>;
  if (id === "paddle") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>P</span>;
  if (id === "square") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>Sq</span>;
  if (id === "creem") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>Cr</span>;
  if (id === "lemonsqueezy") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>🍋</span>;
  if (id === "xero") return <span className={`font-display text-[12px] font-black tracking-tight ${common}`}>X</span>;
  return <CreditCard className={common} />;
};

export default function Subscription({ embedded = false }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("stripe");
  const [cycle, setCycle] = useState("monthly");
  const { currency, setCurrency, fmt: fx, isUsd } = useCurrency();
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState(null);       // current sub state from /billing/current
  const [portalLoading, setPortalLoading] = useState(false);
  const [highlightPlan, setHighlightPlan] = useState(null); // deep-linked plan from landing CTA
  const [manualPay, setManualPay] = useState(null); // PayPal.me manual payment modal

  // 1) Load plans + providers + current billing snapshot on mount
  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => setPlans(r.data));
    api.get("/billing/providers").then((r) => {
      setProviders(r.data || []);
      const firstReady = (r.data || []).find((p) => p.configured);
      if (firstReady) setProvider(firstReady.id);
    });
    api.get("/billing/current").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  // 2) Handle Stripe Checkout return — refresh user + show toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Subscription activated — welcome aboard.");
      // Self-heal: confirm directly with Stripe (covers missed webhooks),
      // then refresh the billing snapshot.
      const refresh = () => api.get("/billing/current").then((r) => setBilling(r.data)).catch(() => {});
      api.post("/billing/reconcile-subscription").then(refresh).catch(refresh);
      // Drop the query string from the URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("checkout") === "cancel") {
      toast.info("Checkout cancelled. No charge was made.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // 3) Deep-link from the landing pricing CTA — ?plan=pro_radio&cycle=yearly
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get("plan");
    const cycleParam = params.get("cycle");
    if (cycleParam === "yearly" || cycleParam === "monthly") setCycle(cycleParam);
    if (planParam) {
      setHighlightPlan(planParam);
      setTimeout(() => {
        document.querySelector(`[data-testid="sub-plan-${planParam}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
    if (planParam || cycleParam) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const current = billing?.plan || user?.subscription_plan;
  const selectedProvider = providers.find((p) => p.id === provider);
  const hasStripeSub = !!billing?.stripe_subscription_id;
  const hasStripeCustomer = !!billing?.stripe_customer_id;

  // Stripe webhook events panel (lazy-loaded — only fetched on first expand)
  const [showEvents, setShowEvents] = useState(false);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const { data } = await api.get("/billing/webhook-events?limit=10");
      setEvents(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load events");
    } finally { setEventsLoading(false); }
  };

  // Open the Stripe Customer Portal — upgrade/downgrade/cancel/update card
  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally { setPortalLoading(false); }
  };

  const isPastDue = (billing?.status || user?.subscription_status) === "past_due";
  const addPaymentMethod = () => {
    if (hasStripeCustomer) return openCustomerPortal();
    document.querySelector('[data-testid="plans-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ensureRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(s);
  });

  const checkout = async (planId) => {
    const plan = plans.find((pp) => pp.id === planId);
    // Free plan — no payment, just flip the user's subscription on the server
    if (plan?.is_free) {
      setBusy(planId);
      try {
        await api.post("/subscriptions/activate-free");
        toast.success("You're on the Free plan");
        // Reload to refresh AuthContext / user.subscription_plan
        setTimeout(() => window.location.reload(), 500);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not activate Free plan");
      } finally { setBusy(null); }
      return;
    }
    if (!selectedProvider?.configured) {
      toast.error(`${selectedProvider?.label || "Provider"} is not configured. Missing: ${selectedProvider?.missing_env?.join(", ") || "credentials"}`);
      return;
    }
    setBusy(planId);
    try {
      const origin_url = window.location.origin;

      // Stripe path — use the proper subscription Checkout (mode=subscription)
      if (provider === "stripe") {
        const { data } = await api.post("/billing/checkout-subscription", {
          plan_id: planId, cycle, origin_url,
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        throw new Error("No checkout URL returned");
      }

      // Legacy gateway path (PayPal / Razorpay / Paddle / Creem / LemonSqueezy)
      const res = await api.post("/billing/checkout", { provider, plan_id: planId, origin_url, mode: "subscription", cycle });
      const data = res.data;
      if (data.provider === "paypal" && data.manual) {
        setManualPay(data);
        setBusy(null);
        return;
      }
      if (data.provider === "razorpay") {
        await ensureRazorpayScript();
        const options = {
          key: data.key_id, amount: data.amount, currency: data.currency,
          name: "AI Radio Content Hub", order_id: data.order_id,
          prefill: { name: data.user_name || "", email: data.user_email || "" },
          handler: () => { window.location.href = `/payment-success?provider=razorpay&session_id=${data.session_id}`; },
          modal: { ondismiss: () => { setBusy(null); } },
          theme: { color: "#facc15" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Checkout failed";
      toast.error(msg);
      setBusy(null);
    }
  };

  const manageBillingBtn = hasStripeSub ? (
    <button
      onClick={openCustomerPortal}
      disabled={portalLoading}
      data-testid="open-billing-portal"
      className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 type-button-label text-cyan-200 hover:bg-cyan-500/20 transition disabled:opacity-50"
    >
      {portalLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
      Manage billing
    </button>
  ) : null;

  return (
    <div className={embedded ? "animate-fade-up" : "p-6 md:p-10 max-w-7xl mx-auto animate-fade-up"} data-testid="subscription-page">
      {!embedded ? (
        <PageHeader
          eyebrow="// BILLING"
          title="Choose your frequency."
          description="Six payment gateways. One subscription. Pick the rails that fit your region."
          action={manageBillingBtn}
        />
      ) : manageBillingBtn ? (
        <div className="mb-4 flex justify-end">{manageBillingBtn}</div>
      ) : null}

      {/* Current subscription card — only when Stripe-backed */}
      {billing?.plan && billing.plan !== "free" && billing.stripe_subscription_id && (
        <div
          className="mb-6 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-start gap-3"
          data-testid="current-subscription-card"
        >
          <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">// CURRENT SUBSCRIPTION</span>
              <span className="font-display text-base font-bold capitalize" data-testid="current-plan-name">{billing.plan}</span>
              <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
                billing.status === "past_due" ? "bg-rose-500/20 text-rose-200" : "bg-emerald-500/20 text-emerald-200"
              }`}>
                {billing.status}
              </span>
              {billing.cycle && (
                <span className="rounded-full bg-bg-elevated px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-ink-secondary border border-line">
                  {billing.cycle}
                </span>
              )}
            </div>
            <div className="mt-1.5 text-xs text-ink-secondary">
              {billing.renewal_date && (
                <>Renews <span className="text-ink-primary font-mono">{new Date(billing.renewal_date).toLocaleDateString()}</span> · </>
              )}
              {billing.payment_status === "failed" ? (
                <span className="text-rose-300">Last payment failed — update your card via Manage billing</span>
              ) : (
                <span>Use <span className="text-cyan-300">Manage billing</span> to upgrade, downgrade, cancel, or update your payment method.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stripe webhook events panel — collapsed by default, lazy-loaded */}
      {hasStripeCustomer && (
        <div className="mb-6 rounded-xl border border-line bg-bg-surface" data-testid="webhook-events-panel">
          <button
            onClick={() => { setShowEvents((v) => !v); if (!events) loadEvents(); }}
            className="w-full flex items-center justify-between gap-3 p-4 hover:bg-bg-elevated/40 transition"
            data-testid="webhook-events-toggle"
          >
            <div className="flex items-center gap-3 text-left">
              <div className="h-8 w-8 rounded-lg bg-cyan-500/15 text-cyan-300 grid place-items-center shrink-0">
                <Globe2 className="h-4 w-4" />
              </div>
              <div>
                <div className="font-mono type-status-badge uppercase tracking-[0.22em] text-cyan-300">// STRIPE WEBHOOK DELIVERY</div>
                <div className="type-list-title text-ink-primary">Last events received from Stripe</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ink-muted">
              {events && (
                <span className="font-mono">{events.total} total {events.verified_by_signature ? "· verified ✓" : "· unsigned (dev)"}</span>
              )}
              <span>{showEvents ? "Hide" : "Show"} ▾</span>
            </div>
          </button>

          {showEvents && (
            <div className="border-t border-line px-4 pb-4">
              {eventsLoading && (
                <div className="py-6 text-center text-ink-muted font-mono text-xs">LOADING...</div>
              )}
              {!eventsLoading && events && events.events.length === 0 && (
                <div className="py-6 text-center text-ink-muted text-xs">
                  No webhook events yet. Complete a test checkout and Stripe will fire delivery within a few seconds.
                </div>
              )}
              {!eventsLoading && events && events.events.length > 0 && (
                <ul className="divide-y divide-line text-xs">
                  {events.events.map((e, i) => (
                    <li key={e.stripe_event_id || i} className="py-3 flex items-start gap-3" data-testid={`webhook-event-${i}`}>
                      <span className={`mt-0.5 rounded-full font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 shrink-0 ${
                        e.type.includes("payment_failed") || e.summary?.startsWith("ERROR")
                          ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                          : e.type.includes("deleted")
                          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      }`}>
                        {e.type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-ink-primary truncate" data-testid={`webhook-event-${i}-summary`}>{e.summary || "—"}</div>
                        <div className="text-[10px] text-ink-muted font-mono mt-0.5">
                          {new Date(e.received_at).toLocaleString()} · {e.stripe_event_id}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={loadEvents}
                disabled={eventsLoading}
                data-testid="webhook-events-refresh"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] hover:text-cyan-300 transition disabled:opacity-50"
              >
                {eventsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "↻"}
                Refresh
              </button>
            </div>
          )}
        </div>
      )}

      {/* Monthly / Yearly toggle */}
      <div className="mb-6 flex flex-wrap items-center justify-center gap-2" data-testid="cycle-toggle">
        <button
          onClick={() => setCycle("monthly")}
          data-testid="cycle-monthly"
          className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition ${
            cycle === "monthly"
              ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
              : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setCycle("yearly")}
          data-testid="cycle-yearly"
          className={`relative rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition ${
            cycle === "yearly"
              ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
              : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
          }`}
        >
          Yearly
          <span className="ml-2 inline-block rounded-full bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 text-[9px] font-bold">
            -2 mos
          </span>
        </button>
        <CurrencyPicker currency={currency} onChange={setCurrency} className="ml-3" />
      </div>
      {!isUsd && (
        <div className="mb-6 -mt-3 text-center text-[10px] font-mono uppercase tracking-[0.15em] text-ink-muted" data-testid="fx-note">
          ≈ shown in {currency} at today's rate — checkout is billed in USD
        </div>
      )}

      {current && (isPastDue ? (
        <div className="mb-8 rounded-xl border border-rose-500/50 bg-rose-500/10 p-5 flex flex-wrap items-center gap-4" data-testid="past-due-banner">
          <div className="h-10 w-10 rounded-lg bg-rose-500/20 grid place-items-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-rose-300" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-display text-lg font-bold text-rose-200" data-testid="past-due-heading">
              Payment failed — action needed
            </h3>
            <div className="mt-1 text-xs text-rose-200/80 leading-relaxed">
              Your <span className="font-semibold uppercase">{current}</span> plan is <span className="font-mono">past_due</span> — the trial ended without a payment method, or a renewal charge failed.
              Add a payment method now; once Stripe's retries run out the subscription gets restricted.
            </div>
          </div>
          <button
            onClick={addPaymentMethod}
            disabled={portalLoading}
            data-testid="add-payment-method-btn"
            className="inline-flex items-center gap-2 rounded-md bg-rose-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-400 transition disabled:opacity-50"
          >
            {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            Add payment method
          </button>
        </div>
      ) : (
        <div className="mb-8 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 flex items-center gap-3" data-testid="current-plan-banner">
          <CreditCard className="h-5 w-5 text-brand-amber" />
          <div>
            <h3 className="type-section-heading text-ink-primary">Currently on {current.toUpperCase()}</h3>
            <div className="type-card-meta-label text-ink-secondary">Status: {billing?.status || user?.subscription_status || "active"}</div>
          </div>
        </div>
      ))}

      <PayPalAwaitingBanner />
      <PayPalAdminApprovals visible={["super_admin", "sysadmin", "owner"].includes(user?.role || "")} />

      {/* Gateway selector */}
      <div className="mb-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="gateway-selector">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber">// PAYMENT GATEWAY</div>
            <h3 className="type-section-heading text-ink-primary mt-1">Where should we route your payment?</h3>
          </div>
          {selectedProvider && (
            <div className="hidden md:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
              <Globe2 className="h-3 w-3" />
              {selectedProvider.regions.join(" · ")}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {providers.filter((p) => p.id !== "xero" && p.configured).map((p) => {
            const active = provider === p.id;
            const disabled = !p.configured;
            return (
              <button
                key={p.id}
                onClick={() => setProvider(p.id)}
                data-testid={`gateway-${p.id}`}
                className={`group relative flex flex-col items-start gap-1.5 rounded-lg border p-3 text-left transition ${
                  active
                    ? "border-brand-amber bg-brand-amber/10"
                    : "border-line bg-bg-elevated hover:border-brand-amber/40 hover:bg-bg-surface"
                } ${disabled ? "opacity-60" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`grid h-6 w-6 place-items-center rounded ${active ? "bg-brand-amber text-black" : "bg-bg-surface text-ink-secondary"}`}>
                    <GatewayMark id={p.id} />
                  </div>
                  <span className="type-list-title text-ink-primary">{p.label}</span>
                </div>
                {disabled ? (
                  <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-ink-muted">
                    <Lock className="h-2.5 w-2.5" /> Configure to enable
                  </span>
                ) : (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-400">{p.manual ? "Ready · PayPal.me" : "Ready"}</span>
                )}
              </button>
            );
          })}
        </div>
        {selectedProvider?.id === "paypal" && selectedProvider?.manual && (
          <div className="mt-4 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-sky-100" data-testid="gateway-paypal-manual-note">
            PayPal payments go to <span className="font-mono text-sky-200">paypal.me/{selectedProvider.paypal_me}</span> — you pay on PayPal,
            then the station owner confirms receipt and your plan activates. Add <span className="font-mono">PAYPAL_CLIENT_ID</span> +{" "}
            <span className="font-mono">PAYPAL_CLIENT_SECRET</span> later to switch to fully automatic activation.
          </div>
        )}
        {selectedProvider && !selectedProvider.configured && (
          <div className="mt-4 rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs text-cyan-100" data-testid="gateway-missing-env">
            <span className="font-mono">{selectedProvider.label}</span> needs these env vars on the backend before it can be used:{" "}
            <span className="font-mono text-cyan-200">{selectedProvider.missing_env.join(", ")}</span>
          </div>
        )}
      </div>

      {user?.founding_tester && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="founding-discount-banner">
          <span className="text-amber-300">★</span>
          <span>
            <b>Founding Tester #{user.founding_cohort_number}</b> — your 50% founding discount is applied automatically at Stripe checkout, on any plan.
          </span>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="plans-grid">
        {plans.map((p) => {
          const highlight = p.popular || p.id === "pro";
          const isCurrent = current === p.id;
          const cyc = p.cycles?.[cycle] || { price: 0, label: "/mo" };
          const monthlyEquiv = cycle === "yearly" ? cyc.monthly_equivalent : null;
          // Free plan bypasses the gateway readiness check
          const disabled = busy === p.id || isCurrent || (!p.is_free && !selectedProvider?.configured);
          return (
            <div
              key={p.id}
              className={`relative rounded-2xl border bg-bg-surface p-6 lift flex flex-col ${
                highlight ? "border-brand-amber/60 lg:scale-[1.03] z-10" : p.id === "network" ? "border-sky-500/50" : "border-line"
              }${highlightPlan === p.id ? " ring-2 ring-brand-amber shadow-[0_0_30px_rgba(245,158,11,0.25)]" : ""}`}
              data-testid={`sub-plan-${p.id}`}
            >
              {highlight && (
                <div className="absolute -top-3 left-6 rounded-full bg-brand-amber px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-black font-bold">
                  Most Popular
                </div>
              )}
              {p.badge && !highlight && (
                <div className="absolute -top-3 left-6 rounded-full bg-cyan-500/90 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-black font-bold">
                  {p.badge}
                </div>
              )}
              <div className="font-display text-2xl font-bold">{p.name}</div>
              {p.tagline && <div className="mt-0.5 text-xs text-ink-muted">{p.tagline}</div>}
              <div className="mt-4 flex flex-wrap items-baseline gap-1">
                {(() => {
                  const priceStr = fx(cyc.price);
                  const size = priceStr.length > 8 ? "text-3xl" : priceStr.length > 5 ? "text-4xl" : "text-5xl";
                  return (
                    <span className={`font-display ${size} font-black tracking-tighter`} data-testid={`sub-price-${p.id}`}>{priceStr}</span>
                  );
                })()}
                <span className="text-ink-muted text-sm">{cyc.label}</span>
              </div>
              {monthlyEquiv ? (
                <div className="mt-1 text-xs text-emerald-300 font-mono">
                  {fx(monthlyEquiv)}/mo billed yearly · {isUsd ? cyc.savings_label : `Save ${fx((p.cycles?.monthly?.price || 0) * 12 - (p.cycles?.yearly?.price || 0))}/yr — 2 months free`}
                </div>
              ) : null}
              <ul className="mt-6 space-y-3 mb-8 flex-1">
                {(p.feature_groups || [{ label: null, items: p.features }]).map((g, gi) => (
                  <React.Fragment key={g.label || gi}>
                    {g.label && (
                      <li className={`font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted ${gi > 0 ? "pt-2 border-t border-line/60" : ""}`} data-testid={`sub-group-${p.id}-${gi}`}>
                        // {g.label}
                      </li>
                    )}
                    {g.items.map((f) => (
                      <li key={f} className="flex items-start gap-2 type-body text-ink-secondary">
                        <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </ul>
              <button
                onClick={() => checkout(p.id)}
                disabled={disabled}
                data-testid={`sub-checkout-${p.id}`}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 type-button-label transition ${
                  isCurrent
                    ? "bg-bg-elevated text-ink-muted cursor-not-allowed"
                    : highlight
                    ? "bg-brand-amber text-black hover:bg-brand-amberHover amber-glow disabled:opacity-50"
                    : p.is_free
                    ? "border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50"
                    : "border border-line bg-bg-elevated text-ink-primary hover:bg-bg-surface disabled:opacity-50"
                }`}
              >
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isCurrent
                  ? "Current plan"
                  : busy === p.id
                  ? p.is_free ? "Activating..." : "Redirecting..."
                  : p.is_free
                  ? "Start free →"
                  : !selectedProvider?.configured
                  ? "Select a ready gateway"
                  : `Subscribe ${cycle === "yearly" ? "yearly" : "monthly"}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Add-ons — standalone pricing for revenue-driving, usage-variable tools */}
      <div className="mt-12" data-testid="addons-section">
        <div className="mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">// ADD-ONS</div>
          <h2 className="font-display text-lg font-bold text-ink-primary mt-1">Add-ons</h2>
          <p className="text-xs text-ink-secondary mt-1 max-w-2xl">
            For revenue-driving tools you may want before you need the whole tier above. Available standalone on Professional and above.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-line bg-bg-surface p-5" data-testid="addon-commercial-creator-card">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-base font-bold text-ink-primary">AI Commercial Creator</span>
              <span className="rounded-md bg-brand-amber/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-brand-amber">Add-on</span>
              {["pro_radio", "network"].includes(current) && (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-300" data-testid="addon-cc-included-badge">
                  Included in your plan
                </span>
              )}
            </div>
            <p className="text-xs text-ink-secondary mt-1.5 mb-4">
              Included free in Pro Radio &amp; Network. Available standalone on Professional.
            </p>
            <ul className="space-y-2.5">
              {[
                <>Subscription: <strong className="text-ink-primary">{fx(49)}/mo</strong> — unlimited generations</>,
                <>Credit pack: <strong className="text-ink-primary">{fx(15)}</strong> for 10 generations, no expiry</>,
                <>3 script variations + AI TTS voice per generation</>,
                <>Auto-ships the mixed spot to Imaging Studio</>,
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-secondary">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {!["pro_radio", "network"].includes(current) && (
              <a
                href="/dashboard/commercials"
                data-testid="addon-cc-get-link"
                className="mt-4 inline-flex items-center gap-2 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-4 py-2 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 transition"
              >
                Get it on the Commercial Creator page →
              </a>
            )}
          </div>
          <div className="rounded-2xl border border-line bg-bg-surface p-5 opacity-60" data-testid="addon-coming-card">
            <div className="font-display text-base font-bold text-ink-primary">More add-ons coming</div>
            <p className="text-xs text-ink-secondary mt-1.5 mb-4">
              Future candidates: usage-variable, revenue-driving tools only — not a home for every module.
            </p>
            <div className="space-y-2 text-sm text-ink-muted">
              <div>Quick Promo Creator (evaluating)</div>
              <div>Music Video Studio (evaluating)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Integrations — accounting/invoicing sync (not a payment gateway) */}
      {providers.some((p) => p.id === "xero" && p.configured) && (
        <div className="mt-12" data-testid="integrations-section">
          <div className="mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300">// INTEGRATIONS</div>
            <h2 className="font-display text-lg font-bold text-ink-primary mt-1">Sync invoices to Xero</h2>
            <p className="text-xs text-ink-secondary mt-1 max-w-2xl">
              Xero is accounting software, not a payment gateway — connect it to raise and track invoices in your Xero organisation.
            </p>
          </div>
          <XeroConnectPanel />
        </div>
      )}

      <div className="mt-8 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">
        Cancel anytime · Encrypted by gateway · No card data touches our servers
      </div>

      <PayPalMePayModal pay={manualPay} onClose={() => setManualPay(null)} />
    </div>
  );
}
